import React, { useRef, useMemo, useCallback } from 'react';
import {
  StyleProp,
  TextStyle,
  ViewStyle,
  Animated,
  Platform,
} from 'react-native';
import { Button as PaperButton, ActivityIndicator } from 'react-native-paper';
import {
  ButtonVariant,
  ButtonSize,
} from '@/shared/styles/components/button/types';
import { getButtonStyles } from '@/shared/styles/utils';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { buttonVariants } from '@/shared/styles/components/button/buttonStyles';
import { Text } from 'react-native-paper';
import styled from '@emotion/native';
import { fontScalingProps } from './StyledText';

type ButtonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  onPress: () => void;
  text: string;
  customStyle?: StyleProp<ViewStyle>;
  customLabelStyle?: StyleProp<TextStyle>;
  disabled?: boolean;
  loading?: boolean;
  compact?: boolean;
  rightIcon?: React.ReactNode;
  leftIcon?: React.ReactNode;
  allowFontScaling?: boolean;
};

// Memoize animated components outside of the component to prevent recreation
const AnimatedPaperButton = Animated.createAnimatedComponent(PaperButton);
const AnimatedText = Animated.createAnimatedComponent(Text);

// Pre-define static style objects
const staticLabelStyle = {
  marginHorizontal: 0,
};

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  onPress,
  text,
  customStyle,
  disabled = false,
  customLabelStyle,
  loading = false,
  compact,
  rightIcon,
  leftIcon,
  allowFontScaling = true,
}) => {
  const { isDesktop } = useBreakpoint();
  const styles = getButtonStyles(variant, size, isDesktop);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const variantConfig = buttonVariants[variant];

  const animateHover = useCallback(
    (toValue: number) => {
      Animated.timing(fadeAnim, {
        toValue,
        duration: 150,
        useNativeDriver: false,
      }).start();
    },
    [fadeAnim],
  );

  const webProps = useMemo(
    () =>
      Platform.select({
        web: {
          onMouseEnter: () => !disabled && animateHover(1),
          onMouseLeave: () => !disabled && animateHover(0),
        },
        default: {},
      }),
    [disabled, animateHover],
  );

  const animatedStyles = useMemo(
    () =>
      !disabled
        ? {
            backgroundColor: fadeAnim.interpolate({
              inputRange: [0, 1],
              outputRange: [
                variantConfig.backgroundColor,
                variantConfig.hoverColor,
              ],
            }),
            borderColor: fadeAnim.interpolate({
              inputRange: [0, 1],
              outputRange: [
                variantConfig.borderColor,
                variantConfig.hoverBorderColor,
              ],
            }),
          }
        : {},
    [disabled, fadeAnim, variantConfig],
  );

  const animatedTextColor = useMemo(
    () =>
      fadeAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [variantConfig.labelColor, variantConfig.hoverLabelColor],
      }),
    [fadeAnim, variantConfig],
  );

  const contentStyle = useMemo(
    () => ({
      height: isDesktop
        ? styles.dimensions.height.desktop
        : styles.dimensions.height.mobile,
      marginVertical: (isDesktop ? -1 : 'auto') as number | 'auto',
    }),
    [isDesktop, styles.dimensions.height],
  );

  const buttonStyleArray = useMemo(
    () => [
      styles.buttonStyle,
      animatedStyles,
      disabled && styles.disabledStyle,
      customStyle,
    ],
    [
      styles.buttonStyle,
      animatedStyles,
      disabled,
      styles.disabledStyle,
      customStyle,
    ],
  );

  const textStyleArray = useMemo(
    () => [
      styles.labelStyle,
      { color: animatedTextColor },
      disabled && styles.disabledLabelStyle,
      loading && styles.loadingLabelStyle,
      customLabelStyle,
    ],
    [
      styles.labelStyle,
      animatedTextColor,
      disabled,
      styles.disabledLabelStyle,
      loading,
      styles.loadingLabelStyle,
      customLabelStyle,
    ],
  );

  return (
    <AnimatedPaperButton
      rippleColor="transparent"
      disabled={disabled}
      mode="contained"
      compact={compact}
      onPress={onPress}
      {...webProps}
      style={buttonStyleArray}
      contentStyle={contentStyle}
      labelStyle={staticLabelStyle}
    >
      {loading && <ActivityIndicator color={variantConfig.labelColor} />}
      {!loading && (
        <Row>
          {leftIcon && leftIcon}
          <AnimatedText
            variant="labelLarge"
            {...(allowFontScaling
              ? fontScalingProps
              : { allowFontScaling: false })}
            style={textStyleArray}
          >
            {text}
          </AnimatedText>
          {rightIcon && rightIcon}
        </Row>
      )}
    </AnimatedPaperButton>
  );
};

const Row = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
});

export default Button;
